import { usePersonsInCompany } from '@commenda/api/persons';
import { SingleCombobox } from '@commenda/kakashi';
import type { IPerson } from '@commenda/types';
import { useMemo } from 'react';
import {
  getKeyPersonRoles,
  KEY_PERSON_ROLES_ENUM_TO_TEXT,
} from '../../../Corporation/KeyPersons/utils';

type RecipientPersonOption = {
  id: string;
  name: string;
  email: string;
  roleLabel: string;
};

type RecipientPickerProps = {
  companyId: number;
  excludedEmails: string[];
  onSelect: (recipient: { name: string; email: string }) => void;
};

const getRoleLabel = (person: IPerson): string => {
  const roleTexts = new Set<string>();
  person.businessEntitiesInfo?.forEach((entityInfo) => {
    getKeyPersonRoles({ ...person, ...entityInfo }).forEach((role) => {
      roleTexts.add(KEY_PERSON_ROLES_ENUM_TO_TEXT[role] || role);
    });
  });
  return Array.from(roleTexts).join(', ');
};

const buildRecipientOptions = (persons: IPerson[]): RecipientPersonOption[] =>
  persons
    .map((person) => ({
      id: String(person.id),
      name: `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim(),
      email: person.email?.trim() ?? '',
      roleLabel: getRoleLabel(person),
    }))
    .filter((option) => option.name.length > 0 && option.email.length > 0);

export function RecipientPicker({
  companyId,
  excludedEmails,
  onSelect,
}: RecipientPickerProps) {
  const { data, isLoading } = usePersonsInCompany(companyId);

  const recipientOptions = useMemo(
    () => buildRecipientOptions(data?.persons ?? []),
    [data]
  );

  const optionsById = useMemo(() => {
    const map = new Map<string, RecipientPersonOption>();
    recipientOptions.forEach((option) => map.set(option.id, option));
    return map;
  }, [recipientOptions]);

  const excludedSet = useMemo(
    () => new Set(excludedEmails.map((email) => email.toLowerCase())),
    [excludedEmails]
  );

  const comboboxOptions = useMemo(
    () =>
      recipientOptions.map((option) => ({
        value: option.id,
        label: `${option.name} ${option.email}`,
        hidden: excludedSet.has(option.email.toLowerCase()),
      })),
    [recipientOptions, excludedSet]
  );

  if (!isLoading && recipientOptions.length === 0) return null;

  const handleSelect = (value: string) => {
    const option = optionsById.get(value);
    if (option) onSelect({ name: option.name, email: option.email });
  };

  return (
    <div className="flex flex-col gap-1.5">
      <span className="txt-small-medium text-text-secondary">
        Choose recipient
      </span>
      <SingleCombobox
        value=""
        setValue={handleSelect}
        options={comboboxOptions}
        disabled={isLoading}
        canClearSelection={false}
        placeholder={isLoading ? 'Loading people…' : 'Select from questionnaire'}
        searchPlaceholder="Search by name or email…"
        notFoundText="No matching people found."
        renderOption={({ option }) => {
          const optionData = optionsById.get(option.value);
          if (!optionData) return null;
          return (
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <div className="flex min-w-0 items-center gap-2">
                <span className="shrink-0 whitespace-nowrap txt-small-medium text-text-primary">
                  {optionData.name}
                </span>
                {optionData.roleLabel ? (
                  <span className="min-w-0 flex-1 truncate txt-xs-regular text-text-tertiary">
                    {optionData.roleLabel}
                  </span>
                ) : null}
              </div>
              <span className="truncate txt-xs-regular text-text-tertiary">
                {optionData.email}
              </span>
            </div>
          );
        }}
      />
    </div>
  );
}
